import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Check, RefreshCw, RotateCcw, Shield } from "lucide-react";
import { StaffApiError, staffErrorMessage } from "../client/api";
import type { PosSale, PublicStoreProfile, UserSession } from "../shared/types";
import { productBrand } from "../brand";

type ApiClient = <T>(path: string, init?: RequestInit) => Promise<T>;

type FeatureResponse = {
  features: {
    phase9FeaturesEnabled: boolean;
    storeCreditRedemptionEnabled?: boolean;
  };
};

type InventorySettingsResponse = {
  locations?: { id: string; name: string; code?: string; active?: boolean }[];
  defaults?: { defaultLocationId?: string };
};

type EligibilityResponse = {
  eligibility: {
    sale: PosSale;
    eligible: boolean;
    reasonCode?: string;
    reason?: string;
    expectedHoldVersion?: number;
    existingReversalId?: string;
    refundMethod: "store_credit";
    fullSaleOnly: true;
    supportedDispositions: ("restock_sellable" | "damaged")[];
  };
};

type ReversalResult = {
  replayed?: boolean;
  reversal: {
    id: string;
    saleId: string;
    accountId: string;
    customerId: string;
    originalLedgerEntryId: string;
    compensatingLedgerEntryId: string;
    originalPaymentAttemptId: string;
    capturedHoldId: string;
    amount: number;
    balanceAmount: number;
    availableBalance: number;
    currency: string;
    status: string;
    restoredLineCount: number;
    restoredQuantity: number;
    sellableQuantity: number;
    damagedQuantity: number;
  };
};

type DispositionDraft = {
  sellableQuantity: number;
  damagedQuantity: number;
  damagedNote: string;
};

type PendingReversal = {
  intent: string;
  key: string;
};

const pendingKey = "spine_pos_store_credit_return_pending";
const reasonOptions = [
  { value: "customer_return", label: "Customer return" },
  { value: "staff_error", label: "Staff error" },
  { value: "duplicate_sale", label: "Duplicate sale" },
  { value: "damaged_after_sale", label: "Damaged after sale" },
  { value: "other", label: "Other" },
] as const;

export function StoreCreditReturnsWorkspace({ api, storeProfile, currentUser }: { api: ApiClient; storeProfile: PublicStoreProfile; currentUser: UserSession }) {
  const [features, setFeatures] = useState<FeatureResponse["features"] | null>(null);
  const [locations, setLocations] = useState<InventorySettingsResponse["locations"]>([]);
  const [defaultLocationId, setDefaultLocationId] = useState("");
  const [sales, setSales] = useState<PosSale[]>([]);
  const [query, setQuery] = useState("");
  const [selectedSale, setSelectedSale] = useState<PosSale | null>(null);
  const [eligibility, setEligibility] = useState<EligibilityResponse["eligibility"] | null>(null);
  const [reasonCode, setReasonCode] = useState<(typeof reasonOptions)[number]["value"]>("customer_return");
  const [note, setNote] = useState("");
  const [dispositions, setDispositions] = useState<Record<string, DispositionDraft>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pending, setPending] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState<ReversalResult["reversal"] | null>(null);
  const canReverse = currentUser.role === "administrator" || currentUser.role === "manager";

  const activeLocation = useMemo(() => {
    return locations?.find((location) => location.id === selectedSale?.locationId)
      ?? locations?.find((location) => location.id === defaultLocationId)
      ?? locations?.[0]
      ?? null;
  }, [defaultLocationId, locations, selectedSale]);

  const filteredSales = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const candidates = sales.filter((sale) => sale.status === "completed");
    if (!normalized) return candidates.slice(0, 25);
    return candidates.filter((sale) => [
      sale.id,
      sale.saleNumber,
      sale.customerId ?? "",
      sale.locationId,
    ].some((field) => field.toLowerCase().includes(normalized))).slice(0, 25);
  }, [query, sales]);

  const totals = useMemo(() => {
    return Object.values(dispositions).reduce((sum, line) => ({
      sellable: sum.sellable + line.sellableQuantity,
      damaged: sum.damaged + line.damagedQuantity,
    }), { sellable: 0, damaged: 0 });
  }, [dispositions]);

  const loadContext = useCallback(async () => {
    const [featureData, settingsData, salesData] = await Promise.all([
      api<FeatureResponse>("/api/pos/features"),
      api<InventorySettingsResponse>("/api/admin/inventory-settings"),
      api<{ sales: PosSale[] }>("/api/pos/sales"),
    ]);
    setFeatures(featureData.features);
    setLocations(settingsData.locations ?? []);
    setDefaultLocationId(settingsData.defaults?.defaultLocationId ?? "");
    setSales(salesData.sales);
  }, [api]);

  useEffect(() => {
    loadContext().catch((err: unknown) => setError(returnErrorMessage(err)));
  }, [loadContext]);

  const selectSale = async (sale: PosSale) => {
    setPending("sale");
    setError("");
    setMessage("");
    setResult(null);
    setConfirmOpen(false);
    try {
      const saleData = await api<{ sale: PosSale }>(`/api/pos/sales/${encodeURIComponent(sale.id)}`);
      const eligibilityData = await api<EligibilityResponse>(`/api/pos/sales/${encodeURIComponent(sale.id)}/store-credit-reversal`);
      setSelectedSale(saleData.sale);
      setEligibility(eligibilityData.eligibility);
      setDispositions(defaultDispositions(saleData.sale));
      setMessage(eligibilityData.eligibility.eligible ? "Sale is eligible for a full store-credit return." : eligibilityData.eligibility.reason ?? "Sale is not eligible.");
    } catch (err) {
      setError(returnErrorMessage(err));
    } finally {
      setPending("");
    }
  };

  const refreshSelected = async () => {
    if (!selectedSale) return;
    await selectSale(selectedSale);
  };

  const setLineDisposition = (lineId: string, draft: DispositionDraft) => {
    setResult(null);
    setDispositions((current) => ({ ...current, [lineId]: draft }));
  };

  const validateReview = (): string | null => {
    if (!selectedSale || !eligibility) return "Select an eligible completed sale first.";
    if (!eligibility.eligible || !eligibility.expectedHoldVersion) return eligibility.reason ?? "This sale is not eligible for reversal.";
    for (const line of selectedSale.lines) {
      const draft = dispositions[line.id];
      if (!draft) return "Every sale line must be accounted for.";
      if (draft.sellableQuantity + draft.damagedQuantity !== line.quantity) return "Every line must return the full original quantity.";
      if (line.copyId && draft.sellableQuantity + draft.damagedQuantity !== 1) return "Exact-copy returns must account for the original sold copy exactly once.";
      if (draft.damagedQuantity > 0 && !draft.damagedNote.trim()) return "Damaged returned units require a bounded staff note.";
    }
    return null;
  };

  const buildIntent = () => {
    if (!selectedSale || !eligibility?.expectedHoldVersion) throw new Error("Missing return intent.");
    return {
      saleId: selectedSale.id,
      expectedStatus: "completed",
      expectedHoldVersion: eligibility.expectedHoldVersion,
      sellableReturnAccepted: true,
      reasonCode,
      note: note.trim() || undefined,
      lines: selectedSale.lines.map((line) => {
        const draft = dispositions[line.id] ?? { sellableQuantity: line.quantity, damagedQuantity: 0, damagedNote: "" };
        const disposition = draft.sellableQuantity > 0 && draft.damagedQuantity > 0
          ? "mixed_sellable_damaged"
          : draft.damagedQuantity > 0
            ? "damaged"
            : "restock_sellable";
        return {
          originalSaleLineId: line.id,
          copyId: line.copyId,
          sellableQuantity: draft.sellableQuantity,
          damagedQuantity: draft.damagedQuantity,
          disposition,
          damagedNote: draft.damagedQuantity > 0 ? draft.damagedNote.trim() : undefined,
        };
      }),
    };
  };

  const submitReturn = async () => {
    const validation = validateReview();
    if (validation) {
      setError(validation);
      return;
    }
    const intent = buildIntent();
    const intentText = JSON.stringify(intent);
    const idempotencyKey = readOrCreatePendingKey(intentText);
    setPending("submit");
    setError("");
    setMessage("");
    try {
      const data = await api<ReversalResult>(`/api/pos/sales/${encodeURIComponent(intent.saleId)}/store-credit-reversal`, {
        method: "POST",
        body: JSON.stringify({ ...intent, idempotencyKey }),
      });
      localStorage.removeItem(pendingKey);
      setResult(data.reversal);
      setConfirmOpen(false);
      setMessage(data.replayed ? "Recovered the existing completed return." : "Full store-credit return completed.");
      await loadContext();
    } catch (err) {
      setError(returnErrorMessage(err));
      if (selectedSale) await refreshSelected().catch(() => undefined);
    } finally {
      setPending("");
    }
  };

  if (features && !features.phase9FeaturesEnabled) {
    return (
      <section className="page returns-workspace">
        <header className="page-header">
          <div>
            <p className="eyebrow">Sales & Returns</p>
            <h1>Full store-credit returns</h1>
          </div>
        </header>
        <div className="empty-state" role="status">
          <AlertTriangle aria-hidden />
          <h2>Phase 9 returns are disabled</h2>
          <p>Full store-credit-only return controls are unavailable in this environment. No legacy cash, Square, partial-return, exchange, or receipt workflow is exposed here.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="page returns-workspace">
      <header className="page-header">
        <div>
          <p className="eyebrow">Sales & Returns</p>
          <h1>Full store-credit returns</h1>
        </div>
        <button onClick={() => void loadContext()} disabled={Boolean(pending)}><RefreshCw /> Refresh</button>
      </header>
      <div className="context-grid">
        <div><span>Product</span><strong>{storeProfile.productName ?? productBrand.name}</strong></div>
        <div><span>Store</span><strong>{storeProfile.storeDisplayName}</strong></div>
        <div><span>Location</span><strong>{activeLocation ? `${activeLocation.name}${activeLocation.code ? ` (${activeLocation.code})` : ""}` : "Backend context required"}</strong></div>
        <div><span>Role</span><strong>{currentUser.role}{canReverse ? " / reversal enabled" : " / read only"}</strong></div>
      </div>
      {error && <div className="error" role="alert">{error}</div>}
      {message && <div className="success" role="status">{message}</div>}
      <div className="workflow-grid">
        <section className="panel">
          <div className="section-title">
            <div>
              <p className="eyebrow">Step 1</p>
              <h2>Find the completed sale</h2>
            </div>
          </div>
          <label className="scan-input">Sale number, sale ID, customer, or location
            <input value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") event.preventDefault(); }} placeholder="Search recent completed POS sales" />
          </label>
          <p className="hint">Lookup is read-only. Multiple matches require deliberate selection.</p>
          <div className="result-list" aria-live="polite">
            {filteredSales.length === 0 ? <p className="empty-inline">No completed sale matches.</p> : filteredSales.map((sale) => (
              <button key={sale.id} className={`result-row ${selectedSale?.id === sale.id ? "active" : ""}`} onClick={() => void selectSale(sale)} disabled={pending === "sale"}>
                <strong>{sale.saleNumber}</strong>
                <span>{formatMoney({ amount: sale.grandTotalAmount, currency: sale.currency })} - {sale.status} - {sale.paymentStatus}</span>
                <small>{sale.customerId ?? "No customer"} - {new Date(sale.completedAt ?? sale.startedAt).toLocaleString()}</small>
              </button>
            ))}
          </div>
        </section>
        <section className="panel">
          <div className="section-title">
            <div>
              <p className="eyebrow">Step 2</p>
              <h2>Eligibility and source evidence</h2>
            </div>
            {selectedSale && <button onClick={() => void refreshSelected()} disabled={Boolean(pending)}><RefreshCw /> Reload sale</button>}
          </div>
          {!selectedSale ? <p className="empty-inline">Select a sale to evaluate its store-credit reversal evidence.</p> : (
            <>
              <div className="summary-grid">
                <div><span>Sale</span><strong>{selectedSale.saleNumber}</strong></div>
                <div><span>Status</span><strong>{selectedSale.status} / {selectedSale.paymentStatus}</strong></div>
                <div><span>Inventory</span><strong>{selectedSale.inventoryStatus}</strong></div>
                <div><span>Refund method</span><strong>{eligibility?.refundMethod === "store_credit" ? "Internal store credit" : "Unavailable"}</strong></div>
                <div><span>Refund amount</span><strong>{formatMoney({ amount: selectedSale.grandTotalAmount, currency: selectedSale.currency })}</strong></div>
                <div><span>Prior return</span><strong>{eligibility?.existingReversalId ?? "None reported"}</strong></div>
              </div>
              <p className={eligibility?.eligible ? "status-ok" : "status-warn"}>
                {eligibility?.eligible ? "Eligible for one full store-credit return." : eligibility?.reason ?? "Not eligible."}
              </p>
              <p className="hint">Partial returns, exchanges, Square refunds, cash refunds, receipts, and manual credit adjustments are not available in this workflow.</p>
            </>
          )}
        </section>
      </div>
      {selectedSale && eligibility && (
        <section className="panel">
          <div className="section-title">
            <div>
              <p className="eyebrow">Step 3</p>
              <h2>Physical return accounting</h2>
            </div>
          </div>
          <div className="table-wrap">
            <table className="responsive-table">
              <thead><tr><th>Line</th><th>Sold</th><th>Sellable</th><th>Damaged</th><th>Damaged note</th></tr></thead>
              <tbody>
                {selectedSale.lines.map((line) => {
                  const draft = dispositions[line.id] ?? { sellableQuantity: line.quantity, damagedQuantity: 0, damagedNote: "" };
                  return (
                    <tr key={line.id}>
                      <td>
                        <strong>{line.title}</strong>
                        <span>{line.format ?? line.trackingMode}{line.copyId ? ` - copy ${line.copyId}` : ""}</span>
                      </td>
                      <td>{line.quantity}</td>
                      <td>
                        <input aria-label={`Sellable quantity for ${line.title}`} type="number" min={0} max={line.quantity} value={draft.sellableQuantity} disabled={!eligibility.eligible || Boolean(result)} onChange={(event) => {
                          const sellableQuantity = clampInteger(Number(event.target.value), 0, line.quantity);
                          setLineDisposition(line.id, { ...draft, sellableQuantity, damagedQuantity: Math.max(0, line.quantity - sellableQuantity) });
                        }} />
                      </td>
                      <td>
                        <input aria-label={`Damaged quantity for ${line.title}`} type="number" min={0} max={line.quantity} value={draft.damagedQuantity} disabled={!eligibility.eligible || Boolean(result)} onChange={(event) => {
                          const damagedQuantity = clampInteger(Number(event.target.value), 0, line.quantity);
                          setLineDisposition(line.id, { ...draft, damagedQuantity, sellableQuantity: Math.max(0, line.quantity - damagedQuantity) });
                        }} />
                      </td>
                      <td>
                        <input aria-label={`Damaged note for ${line.title}`} value={draft.damagedNote} maxLength={500} disabled={!eligibility.eligible || draft.damagedQuantity === 0 || Boolean(result)} onChange={(event) => setLineDisposition(line.id, { ...draft, damagedNote: event.target.value })} placeholder={draft.damagedQuantity > 0 ? "Required for damaged units" : "Not required"} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="summary-grid">
            <div><span>Sellable restored</span><strong>{totals.sellable}</strong></div>
            <div><span>Damaged recorded</span><strong>{totals.damaged}</strong></div>
            <div><span>Total accounted</span><strong>{totals.sellable + totals.damaged}</strong></div>
          </div>
          <div className="form-grid">
            <label>Reason code
              <select value={reasonCode} onChange={(event) => setReasonCode(event.target.value as typeof reasonCode)} disabled={!eligibility.eligible || Boolean(result)}>
                {reasonOptions.map((reason) => <option key={reason.value} value={reason.value}>{reason.label}</option>)}
              </select>
            </label>
            <label className="wide">Internal note
              <input value={note} maxLength={500} onChange={(event) => setNote(event.target.value)} disabled={!eligibility.eligible || Boolean(result)} placeholder="Optional bounded note" />
            </label>
          </div>
          <div className="actions">
            {!canReverse && <span className="status-warn"><Shield aria-hidden /> Manager or administrator confirmation is required.</span>}
            <button className="primary" onClick={() => {
              const validation = validateReview();
              if (validation) setError(validation);
              else {
                setError("");
                setConfirmOpen(true);
              }
            }} disabled={!eligibility.eligible || !canReverse || Boolean(result) || Boolean(pending)}>
              <RotateCcw /> Review full return
            </button>
          </div>
        </section>
      )}
      {confirmOpen && selectedSale && eligibility && (
        <div className="modal-backdrop" role="presentation">
          <section className="modal" role="dialog" aria-modal="true" aria-labelledby="return-confirm-title">
            <h2 id="return-confirm-title">Confirm full store-credit return</h2>
            <p>This will restore the full original store-credit amount and account for every sold unit. The original sale, hold, debit, payment attempt, receipt snapshots, tax snapshots, discount snapshots and sale movements remain immutable.</p>
            <dl>
              <dt>Sale</dt><dd>{selectedSale.saleNumber}</dd>
              <dt>Amount</dt><dd>{formatMoney({ amount: selectedSale.grandTotalAmount, currency: selectedSale.currency })}</dd>
              <dt>Sellable units</dt><dd>{totals.sellable}</dd>
              <dt>Damaged units</dt><dd>{totals.damaged}</dd>
              <dt>Expected hold version</dt><dd>{eligibility.expectedHoldVersion}</dd>
            </dl>
            <div className="warning">Do not continue if a refund is uncertain elsewhere. This screen will not create cash, Square, mixed-tender, partial-return, exchange, or receipt behavior.</div>
            <div className="actions">
              <button onClick={() => setConfirmOpen(false)} disabled={pending === "submit"}>Cancel</button>
              <button className="primary" onClick={() => void submitReturn()} disabled={pending === "submit"}><Check /> Confirm full return</button>
            </div>
          </section>
        </div>
      )}
      {result && (
        <section className="panel">
          <div className="section-title"><div><p className="eyebrow">Completed evidence</p><h2>Read-only return result</h2></div></div>
          <div className="summary-grid">
            <div><span>Reversal</span><strong>{result.id}</strong></div>
            <div><span>Ledger credit</span><strong>{result.compensatingLedgerEntryId}</strong></div>
            <div><span>Refunded credit</span><strong>{formatMoney({ amount: result.amount, currency: result.currency })}</strong></div>
            <div><span>Available balance</span><strong>{formatMoney({ amount: result.availableBalance, currency: result.currency })}</strong></div>
            <div><span>Sellable restored</span><strong>{result.sellableQuantity}</strong></div>
            <div><span>Damaged recorded</span><strong>{result.damagedQuantity}</strong></div>
          </div>
          <p className="hint">Provider refund status: not applicable. Reconciliation status: local store-credit reversal completed by server authority.</p>
          <button onClick={() => {
            setSelectedSale(null);
            setEligibility(null);
            setDispositions({});
            setResult(null);
            setMessage("");
            setError("");
          }}>Start another return</button>
        </section>
      )}
    </section>
  );
}

function defaultDispositions(sale: PosSale): Record<string, DispositionDraft> {
  return Object.fromEntries(sale.lines.map((line) => [line.id, { sellableQuantity: line.quantity, damagedQuantity: 0, damagedNote: "" }]));
}

function readOrCreatePendingKey(intent: string): string {
  const existing = safeParsePending(localStorage.getItem(pendingKey));
  if (existing?.intent === intent) return existing.key;
  const next: PendingReversal = { intent, key: crypto.randomUUID() };
  localStorage.setItem(pendingKey, JSON.stringify(next));
  return next.key;
}

function safeParsePending(value: string | null): PendingReversal | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as PendingReversal;
    return parsed && typeof parsed.intent === "string" && typeof parsed.key === "string" ? parsed : null;
  } catch {
    return null;
  }
}

function returnErrorMessage(error: unknown): string {
  if (error instanceof StaffApiError) return staffErrorMessage(error);
  if (error instanceof Error) return error.message;
  return "The return state could not be confirmed. Preserve the selected sale and refresh before retrying.";
}

function clampInteger(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, Math.trunc(value)));
}

function formatMoney(money?: { amount: number; currency: string }) {
  if (!money) return "-";
  return new Intl.NumberFormat(undefined, { style: "currency", currency: money.currency }).format(money.amount / 100);
}
